<template>
    <div>
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h4 class="mb-0 font-weight-bold">{{ $t('coop_my_applications') }}</h4>
            <b-button variant="primary"
                      v-if="checkPermission('coopadvisorappointment_store')"
                      @click="openNewApplicationModal">
                {{ $t('coop_new_application') }}
            </b-button>
        </div>

        <!-- Applications Table -->
        <datatable :isLoading.sync="datatable.isLoading"
                   :columns="datatable.columns"
                   :rows="datatable.rows"
                   :total="datatable.total"
                   :queryParams="datatable.queryParams"
                   :lineNumbers="false"
                   @on-page-change="onPageChange"
                   @on-sort-change="onSortChange"
                   @on-per-page-change="onPerPageChange"
                   v-show="datatable.showTable"
        />

        <!-- New Application / Calendar Modal -->
        <CommonModal ref="newApplicationModal" size="xl" :onHideOnlyX="true">
            <template v-slot:CommonModalTitle>
                {{ $t('coop_appointment_calendar') }}
            </template>
            <template v-slot:CommonModalContent>
                <div class="p-2">
                    <div class="calendar-container p-4 rounded mb-4" style="background-color: #f7f3ed; border: 1px solid #e6dfd5;">
                        <div class="d-flex justify-content-between align-items-center mb-4">
                            <!-- Week Navigation -->
                            <div class="d-flex align-items-center">
                                <b-button variant="light"
                                          size="sm"
                                          class="rounded-circle shadow-sm border mr-3"
                                          style="width: 32px; height: 32px; padding: 0;"
                                          @click="navigateWeek(-1)">
                                    <i class="ri-arrow-left-s-line" style="font-size: 1.2rem; line-height: 1;"></i>
                                </b-button>
                                <span class="font-weight-bold text-secondary">{{ getWeekRangeLabel() }}</span>
                                <b-button variant="light"
                                          size="sm"
                                          class="rounded-circle shadow-sm border ml-3"
                                          style="width: 32px; height: 32px; padding: 0;"
                                          @click="navigateWeek(1)">
                                    <i class="ri-arrow-right-s-line" style="font-size: 1.2rem; line-height: 1;"></i>
                                </b-button>
                            </div>
                        </div>

                        <!-- Calendar Grid -->
                        <b-row class="text-center mx-0 bg-white p-3 rounded shadow-sm">
                            <b-col v-for="day in weekDays"
                                   :key="day.dateStr"
                                   class="px-1 py-2 border-right last-no-border"
                                   style="min-width: 110px;">
                                <div class="font-weight-bold text-dark mb-1" style="font-size: 0.9rem;">
                                    {{ day.dayName }}
                                </div>
                                <div class="text-muted mb-3" style="font-size: 0.8rem;">
                                    {{ day.formattedDate }}
                                </div>

                                <div v-if="day.slots && day.slots.length > 0">
                                    <b-button v-for="slot in day.slots"
                                              :key="slot.time"
                                              size="sm"
                                              block
                                              :variant="getSlotVariant(slot)"
                                              :disabled="slot.status !== 'available'"
                                              class="mb-2 py-1 font-weight-bold slot-btn"
                                              @click="selectSlot(day, slot)">
                                        {{ slot.time }}
                                    </b-button>
                                </div>
                                <div v-else class="text-muted py-3 font-italic" style="font-size: 0.8rem;">
                                    -
                                </div>
                            </b-col>
                        </b-row>
                    </div>
                </div>
            </template>
        </CommonModal>
    </div>
</template>

<script>
    import Datatable from '@/components/datatable/Datatable';
    import CommonModal from '@/components/elements/CommonModal';
    import CoopAdvisorAppointmentService from '@/services/CoopAdvisorAppointmentService';
    import moment from 'moment';
    import qs from 'qs';

    export default {
        name: 'AdvisorAppointments',
        components: {
            Datatable,
            CommonModal
        },
        data() {
            return {
                currentWeekOffset: 0,
                weekDays: [],
                datatable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('delete'),
                                    class: 'ri-delete-bin-line text-danger',
                                    permission: 'coopadvisorappointment_delete',
                                    callback: (row) => {
                                        this.deleteAppointment(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('date'),
                            field: 'appointment_date',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_event_time'),
                            field: 'appointment_time',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('status'),
                            field: this.getLocaleField('status_name'),
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: '-id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        created() {
            this.getRows();
        },
        methods: {
            getRows() {
                if (!this.checkPermission('coopadvisorappointment_me')) {
                    return;
                }
                this.datatable.isLoading = true;
                const config = {
                    params: { ...this.datatable.queryParams },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                CoopAdvisorAppointmentService.getMe(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination?.total || response.data.data.length;
                        this.datatable.showTable = true;
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.getRows();
            },
            onSortChange(params) {
                if (params && params.length > 0) {
                    this.datatable.queryParams.sort = (params[0].type === 'desc' ? '-' : '') + params[0].field;
                }
                this.getRows();
            },
            openNewApplicationModal() {
                this.currentWeekOffset = 0;
                this.loadWeekSlots();
                this.$refs.newApplicationModal.$refs.commonModal.show();
            },
            navigateWeek(offset) {
                this.currentWeekOffset += offset;
                this.loadWeekSlots();
            },
            getWeekRangeLabel() {
                const startOfWeek = moment().add(this.currentWeekOffset, 'weeks').startOf('isoWeek');
                const endOfWeek = moment().add(this.currentWeekOffset, 'weeks').endOf('isoWeek');
                return `${startOfWeek.format('DD.MM.YYYY')} - ${endOfWeek.format('DD.MM.YYYY')}`;
            },
            loadWeekSlots() {
                const startOfWeek = moment().add(this.currentWeekOffset, 'weeks').startOf('isoWeek');
                const endOfWeek = moment().add(this.currentWeekOffset, 'weeks').endOf('isoWeek');
                const startDateStr = startOfWeek.format('YYYY-MM-DD');
                const endDateStr = endOfWeek.format('YYYY-MM-DD');

                const config = {
                    params: {
                        start_date: startDateStr,
                        end_date: endDateStr
                    }
                };

                CoopAdvisorAppointmentService.getAvailableSlots(config)
                    .then((response) => {
                        const slotsData = response.data?.data || response.data || [];
                        const days = [];
                        for (let i = 0; i < 7; i++) {
                            const date = moment().add(this.currentWeekOffset, 'weeks').startOf('isoWeek').add(i, 'days');
                            const dateStr = date.format('YYYY-MM-DD');
                            const foundDay = Array.isArray(slotsData) ? slotsData.find(d => d.date === dateStr) : null;
                            const rawSlots = foundDay?.slots || [];

                            const daySlots = rawSlots.map(s => {
                                if (typeof s === 'string') {
                                    return { time: s, status: 'available' };
                                }
                                return {
                                    time: (s.start_time || s.time || '').substring(0, 5),
                                    status: (s.available === false || s.is_unavailable) ? 'unavailable' : (s.status || 'available')
                                };
                            });

                            days.push({
                                dateStr: dateStr,
                                dayName: date.locale(this.$i18n.locale).format('dddd'),
                                formattedDate: date.format('DD.MM.YYYY'),
                                slots: daySlots
                            });
                        }
                        this.weekDays = days;
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    });
            },
            getSlotVariant(slot) {
                if (slot.status === 'available') return 'outline-success';
                if (slot.status === 'booked') return 'secondary';
                return 'light';
            },
            selectSlot(day, slot) {
                if (slot.status !== 'available') return;

                this.$swal.fire({
                    text: this.$t('coop_advisor_appointment_book_confirm'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        const payload = {
                            appointment_date: day.dateStr,
                            appointment_time: slot.time
                        };
                        CoopAdvisorAppointmentService.bookAppointment(payload)
                            .then((response) => {
                                this.$toast.success(this.$t('api.' + response.data.message));
                                this.$refs.newApplicationModal.$refs.commonModal.hide();
                                this.getRows();
                            })
                            .catch((error) => {
                                this.showErrors(error, null);
                            });
                    }
                });
            },
            deleteAppointment(row) {
                this.$swal.fire({
                    text: this.$t('coop_advisor_appointment_delete_confirm'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        CoopAdvisorAppointmentService.remove(row.id)
                            .then((response) => {
                                this.$toast.success(this.$t('api.' + response.data.message));
                                this.getRows();
                            })
                            .catch((error) => {
                                this.showErrors(error, null);
                            });
                    }
                });
            }
        }
    };
</script>

<style scoped>
    .last-no-border:last-child {
        border-right: none !important;
    }
</style>
